import { useState } from 'react';
import { tr } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Marque d'un travail fait en plus de l'offre. */
export const EN_PLUS = 'En plus : ';

/**
 * Les travaux cochés, écrits pour le rapport : un poste par ligne, les travaux
 * hors offre signalés comme tels.
 *
 * @param {string[]} choisis postes cochés et travaux ajoutés
 * @returns {string} les lignes, ou une chaîne vide
 */
export function lignesTravaux(choisis) {
  return (choisis || []).map((t) => (t.startsWith(EN_PLUS) ? '➕ ' + t : '✔ ' + t)).join('\n');
}

/**
 * Ce que l'ouvrier a fait, choisi parmi les postes de l'offre du chantier : il
 * en coche plusieurs d'un geste, et ajoute un travail fait en plus, hors offre.
 * Le patron lit ainsi ce qui a été fait poste par poste, et voit tout de
 * suite le travail supplémentaire, à facturer en avenant.
 *
 * @param {object} props propriétés du composant
 * @param {string[]} props.postes postes du chantier (lignes de l'offre)
 * @param {string[]} props.choisis postes cochés et travaux ajoutés
 * @param {(choisis: string[]) => void} props.onChange reçoit la nouvelle sélection
 * @param {string} [props.titre] libellé au-dessus des postes
 * @returns {import('react').ReactElement} le choix des travaux
 */
export function ChoixTravaux({ postes, choisis, onChange, titre = 'Travaux de l’offre faits' }) {
  const [enPlus, setEnPlus] = useState('');
  const liste = postes || [];
  const coches = choisis || [];
  const ajoutes = coches.filter((t) => t.startsWith(EN_PLUS));
  const bascule = (p) =>
    onChange(coches.includes(p) ? coches.filter((x) => x !== p) : [...coches, p]);
  const ajoute = () => {
    const t = enPlus.trim();
    if (!t) return;
    const v = EN_PLUS + t;
    if (!coches.includes(v)) onChange([...coches, v]);
    setEnPlus('');
  };
  const pastille = (on, plus) => ({
    padding: '8px 12px',
    borderRadius: 18,
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
    border: '1px solid ' + (on ? (plus ? C.amber : C.navy) : C.border),
    background: on ? (plus ? C.amber : C.navy) : C.white,
    color: on ? '#fff' : C.ink,
  });
  return (
    <div style={{ marginBottom: 11 }}>
      {liste.length > 0 && (
        <>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: C.muted,
              marginBottom: 5,
              textTransform: 'uppercase',
            }}
          >
            {tr(titre)}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {liste.map((p) => {
              const on = coches.includes(p);
              return (
                <div
                  key={p}
                  role="checkbox"
                  aria-checked={on}
                  onClick={() => bascule(p)}
                  style={pastille(on, false)}
                >
                  {on ? '✓ ' : ''}
                  {p}
                </div>
              );
            })}
          </div>
        </>
      )}
      {/* Sans poste, la liste disparaissait sans un mot : l'ouvrier cherchait
          les articles de l'offre. */}
      {liste.length === 0 && (
        <div
          data-testid="sans-postes"
          style={{
            fontSize: 11,
            color: C.muted,
            background: C.bg,
            borderRadius: 10,
            padding: '9px 11px',
            marginBottom: 8,
          }}
        >
          {tr('Ce chantier n’a pas d’offre acceptée : écris ce que tu as fait ci-dessous.')}
        </div>
      )}
      {ajoutes.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {ajoutes.map((t) => (
            <div
              key={t}
              role="checkbox"
              aria-checked
              onClick={() => bascule(t)}
              style={pastille(true, true)}
            >
              ➕ {t.slice(EN_PLUS.length)} ✕
            </div>
          ))}
        </div>
      )}
      {/* Les postes cochés n'ont rien à « ajouter » : ils partent avec le
          rapport. Le bouton ne sert qu'au travail fait en plus, et n'apparaît
          que quand il est écrit — grisé, il laissait croire qu'il fallait
          l'activer pour garder les postes cochés. */}
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: C.muted,
          margin: '4px 0 5px',
          textTransform: 'uppercase',
        }}
      >
        {tr('Travail fait en plus, hors offre (facultatif)')}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          aria-label={tr('Travail fait en plus (hors offre)')}
          value={enPlus}
          onChange={(e) => setEnPlus(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              ajoute();
            }
          }}
          placeholder={tr('Ex. réparation d’une fenêtre')}
          style={{
            flex: 1,
            minWidth: 0,
            border: '1px solid ' + C.border,
            borderRadius: 10,
            padding: '10px 11px',
            fontSize: 12,
          }}
        />
        {enPlus.trim() ? (
          <button
            type="button"
            onClick={ajoute}
            style={{
              border: 'none',
              borderRadius: 10,
              padding: '0 14px',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
              background: C.amber,
              color: '#fff',
            }}
          >
            {tr('＋ Ajouter')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
